import { useEffect, useRef, useState } from 'react';
import { FlaskConical, RotateCcw } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { V2AdvisorReportView } from '../components/V2AdvisorReportView';
import { V2ReportView } from '../components/V2ReportView';
import {
  isSimulatorOff,
  isStaleDraft,
  useSimulatorReports,
  type ReportDraft,
} from './simulatorApi';
import { Notice, SimulatorUnavailable } from './SimulatorNotice';

/**
 * Both reports for a finished simulation: tabs on one page, the
 * client's first, each under a line saying whose view it is. The views are
 * the real ones, unedited — no status buttons (no handler is passed), no PDF
 * (out of scope), no way back to a dashboard the visitor does not have.
 *
 * Posted from the draft on every visit, since nothing is stored server-side;
 * the draft itself is only cleared by Start over or by `onStale`.
 */

// ponytail: draft copy — the tab labels, framing lines and confirm text belong to the copy pass.
const TABS = {
  client: {
    label: 'What your client sees',
    framing: 'This is the report your client would see after taking the assessment.',
  },
  advisor: {
    label: 'What you see',
    framing:
      "This is your planning view of the same answers: your client's top three needs, and what's coming next.",
  },
} as const;

type SimulatorReportsProps = {
  draft: ReportDraft;
  /** Clears the draft and goes back to the landing. */
  startOver: () => void;
  /** The answers no longer fit the content: drop them and say the simulation expired. */
  onStale: () => void;
};

function FramingLine({ text }: { text: string }) {
  return (
    <div className="mb-5 flex items-start gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2">
      <FlaskConical className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden />
      <p className="text-[13px] leading-snug text-gray-600">
        <span className="font-semibold text-gray-700">Simulation.</span> {text}
      </p>
    </div>
  );
}

/**
 * Redoing it means Discovery and five pillars again, from a spot that invites
 * misclicks — so it asks first, with keeping the reports as the emphasized
 * choice (the Navigator's "Restart guide" precedent).
 */
function StartOverButton({ onConfirm }: { onConfirm: () => void }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-2">
          <RotateCcw className="h-4 w-4" aria-hidden />
          Start over
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Start the simulation over?</AlertDialogTitle>
          <AlertDialogDescription>
            This clears your answers in this browser and takes you back to the beginning, so you
            would answer the discovery questions and all five pillars again.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {/* Cancel first, as in the app's other AlertDialogs: the safe choice
            leads the tab order and color carries the emphasis. */}
        <AlertDialogFooter>
          <AlertDialogCancel className="border-0 bg-[#026857] text-white hover:bg-[#025246] focus:ring-[#026857]">
            Keep my reports
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400"
          >
            Yes, start over
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function SimulatorReports({ draft, startOver, onStale }: SimulatorReportsProps) {
  const { data, isLoading, error, refetch } = useSimulatorReports(draft);
  const [tab, setTab] = useState<keyof typeof TABS>('client');
  const stale = isStaleDraft(error);

  // The parent passes a fresh callback on every render; keep the latest in a
  // ref so a re-render can't report the same stale draft twice.
  const onStaleRef = useRef(onStale);
  useEffect(() => {
    onStaleRef.current = onStale;
  });
  useEffect(() => {
    if (stale) onStaleRef.current();
  }, [stale]);

  if (isLoading || stale) return <Notice>Preparing your reports…</Notice>;
  // Kill switch turned off since the content loaded: same dead end as the page.
  if (isSimulatorOff(error)) return <SimulatorUnavailable />;
  if (!data)
    return (
      <Notice>
        <p>Unable to prepare your reports. Your answers are still saved.</p>
        <Button type="button" variant="outline" className="mt-4" onClick={() => refetch()}>
          Try again
        </Button>
      </Notice>
    );

  return (
    <div className="mx-auto max-w-4xl">
      <Tabs value={tab} onValueChange={(value) => setTab(value as keyof typeof TABS)}>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="client">{TABS.client.label}</TabsTrigger>
            <TabsTrigger value="advisor">{TABS.advisor.label}</TabsTrigger>
          </TabsList>
          <StartOverButton onConfirm={startOver} />
        </div>
        <TabsContent value="client">
          <FramingLine text={TABS.client.framing} />
          <V2ReportView report={data.clientReport} />
        </TabsContent>
        <TabsContent value="advisor">
          <FramingLine text={TABS.advisor.framing} />
          <V2AdvisorReportView report={data.advisorReport} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
